import React from 'react';
import { 
  CheckCircle, 
  Users, 
  Target,
  Calendar,
  Rocket,
  Settings,
  Zap,
  TrendingUp
} from 'lucide-react';

const Oct27Report = () => {
  return (
    <div className="status-report">
      <div className="report-header">
        <h1 className="report-title">
          <Calendar className="w-8 h-8 text-blue-600" />
          Executive Update - October 27th, 2025
        </h1>
        <div className="report-meta">
          <span className="meta-item">
            <Calendar className="w-4 h-4" />
            October 27, 2025
          </span>
          <span className="meta-item">
            <Target className="w-4 h-4" />
            Executive Summary
          </span>
        </div>
      </div>

      <div className="report-grid">
        {/* Production Deployments */}
        <div className="report-section">
          <h2 className="section-title">
            <Rocket className="w-6 h-6 text-green-600" />
            Production Deployments
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Deployed Today (October 27)</h3>
                <span className="status-badge completed">DEPLOYED</span>
              </div>
              <ul className="achievement-list">
                <li>Enhanced prompt logic for multiple page number extraction</li>
                <li>Improved handling of documents without coverage information</li>
                <li>Updated error messaging for better user clarity</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Scheduled Deployment (October 16)</h3>
                <span className="status-badge scheduled">COMPLETED</span>
              </div>
              <ul className="achievement-list">
                <li>AIML API enhancement: Adding 'user_id' parameter</li>
                <li>Timeline: 3:30 PM - 4:15 PM IST</li>
                <li>Successfully deployed to production</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Engineering Excellence */}
        <div className="report-section">
          <h2 className="section-title">
            <Settings className="w-6 h-6 text-purple-600" />
            Engineering Excellence
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Team Collaboration</h3>
                <span className="status-badge completed">COMPLETED</span>
              </div>
              <ul className="achievement-list">
                <li>Completed engineering excellence activities between Naqi and Dhruv</li>
                <li>Code review and quality standards enforcement</li>
                <li>Best practices implementation and enhanced development workflows</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Weekly Progress Highlights */}
        <div className="report-section">
          <h2 className="section-title">
            <TrendingUp className="w-6 h-6 text-blue-600" />
            Weekly Progress Highlights (October 6-10, 2025)
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Binder Ground Truth</h3>
                <span className="status-badge completed">COMPLETED</span>
              </div>
              <ul className="achievement-list">
                <li>Completed with all 55 documents</li>
                <li>High accuracy ground truth dataset established</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Contract Documents</h3>
                <span className="status-badge in-progress">IN PROGRESS</span>
              </div>
              <ul className="achievement-list">
                <li>Ground truth preparation started</li>
                <li>Ongoing development of training data</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Kinetic</h3>
                <span className="status-badge in-progress">IN PROGRESS</span>
              </div>
              <ul className="achievement-list">
                <li>Adding Project module implementation ongoing</li>
                <li>Single project upload capability ready</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Injala One</h3>
                <span className="status-badge in-progress">IN PROGRESS</span>
              </div>
              <ul className="achievement-list">
                <li>MCP exploration initiated</li>
                <li>Early stage research and development</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Product Readiness */}
        <div className="report-section">
          <h2 className="section-title">
            <CheckCircle className="w-6 h-6 text-emerald-600" />
            Product Readiness Status
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Kinetic</h3>
                <span className="status-badge ready">READY</span>
              </div>
              <ul className="achievement-list">
                <li>Phase 1 with single project upload capability operational</li>
                <li>Production-ready deployment</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Asuretify</h3>
                <span className="status-badge ready">READY</span>
              </div>
              <ul className="achievement-list">
                <li>Full-scale QA completed</li>
                <li>Production deployment ready</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Internal Tool Development */}
        <div className="report-section">
          <h2 className="section-title">
            <Zap className="w-6 h-6 text-orange-600" />
            Internal Tool Development
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>New Tool Created</h3>
                <span className="status-badge completed">COMPLETED</span>
              </div>
              <ul className="achievement-list">
                <li>Created last week with demonstration completed</li>
                <li>Purpose: Support Baldwin and Prequal work</li>
                <li>Ready for operational use</li>
                <li>Enhanced workflow efficiency implemented</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Baldwin Pilot Updates */}
        <div className="report-section">
          <h2 className="section-title">
            <Users className="w-6 h-6 text-indigo-600" />
            Baldwin Pilot Updates
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Feedback Received</h3>
                <span className="status-badge in-progress">RECEIVED</span>
              </div>
              <ul className="achievement-list">
                <li>Significant feedback received on Thursday</li>
                <li>Key concerns: Low usage and quotes</li>
                <li>Concerns will be discussed in meeting</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Issue Resolution</h3>
                <span className="status-badge completed">FIXED</span>
              </div>
              <ul className="achievement-list">
                <li>Problems fixed as of Thursday</li>
                <li>Awaiting team usage to validate improvements</li>
                <li>Active usage tracking and monitoring in place</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Summary & Next Steps */}
        <div className="report-section">
          <h2 className="section-title">
            <Target className="w-6 h-6 text-teal-600" />
            Summary & Next Steps
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Completed This Period</h3>
                <span className="status-badge completed">✓</span>
              </div>
              <ul className="achievement-list">
                <li>Production deployments with enhanced logic</li>
                <li>Engineering excellence activities</li>
                <li>55 binder documents ground truth completed</li>
                <li>Internal tool development for Baldwin/Prequal</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Key Priorities</h3>
                <span className="status-badge in-progress">ONGOING</span>
              </div>
              <ul className="achievement-list">
                <li>Monitor Baldwin pilot feedback and usage</li>
                <li>Continue contract documents ground truth development</li>
                <li>Complete Kinetic Phase 1 deployment</li>
                <li>Support Asuretify production release</li>
              </ul>
            </div>
          </div>
        </div>

      </div>

      <div className="report-footer">
        <p><strong>Prepared by:</strong> Naqi & Claude</p>
        <p><strong>Report Date:</strong> October 27, 2025</p>
      </div>
    </div>
  );
};

export default Oct27Report;



